/**
 * Ambassador Pack Editor V4.0 - Internal/External Ambassador Management
 * Matches Product Pack V4 UI/UX style with numbered sections and gradient header
 * 
 * 5 Sections:
 * 1. Basic Info (Category, Type, Name, Role, Bio, Photo, Credibility)
 * 2. Story & Background (Journey, Pain Points, Turning Points, Values)
 * 3. Usage Rights & Legal (Permissions, Contract, Compliance)
 * 4. Role Profiles (Sales/Branding/Awareness with strength scores)
 * 5. Content Assets (Quotes, Testimonials, Success Stories, Social Proof)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import {
  createAmbassadorPackV4,
  getAmbassadorPackV4,
  updateAmbassadorPackV4,
  publishAmbassadorPackV4,
  unpublishAmbassadorPackV4
} from '../api/ambassadorPacksV4';
import { EMZI_COLORS, EMZI_RADIUS } from '../styles/emziCI';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

const AmbassadorPackEditorV4 = () => {
  const { packId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = Boolean(packId);

  // States
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [openSections, setOpenSections] = useState({
    basic_info: true,
    story: false,
    legal: false,
    roles: false,
    assets: false
  });

  // Draft data for each section
  const [draftBasicInfo, setDraftBasicInfo] = useState({
    ambassador_category: 'external',
    ambassador_type: 'external_expert',
    name: '',
    role_title: '',
    profile_photo_url: '',
    short_bio: '',
    credibility_markers: [],
    email: '',
    phone: '',
    social_media: {},
    persona_brand: '',
    market_influence_category: 'micro',
    audience_demographics: {}
  });

  const [draftStory, setDraftStory] = useState({
    full_story: '',
    pain_points_overcome: [],
    turning_points: [],
    current_achievement: '',
    values_beliefs: [],
    story_themes: [],
    voice_tone_profile: {
      primary_tone: 'warm',
      secondary_tone: null,
      formality_level: 50,
      energy_level: 50,
      technicality_level: 0,
      speaking_pace: 'moderate',
      sentence_structure: 'medium_balanced',
      catchphrases: [],
      communication_quirks: []
    },
    emotional_signature: {
      primary_emotion: 'trust',
      secondary_emotion: null,
      emotional_range: [],
      emotional_intensity: 50
    }
  });

  const [draftLegal, setDraftLegal] = useState({
    name_mention: true,
    story_sharing: false,
    image_visual: false,
    testimonial_quote: true,
    platform_tag: false,
    video_audio: false,
    legal_clearance_status: 'pending',
    contract_reference_id: '',
    contract_start_date: null,
    contract_end_date: null,
    compensation_type: null,
    usage_restrictions: [],
    geographic_restrictions: [],
    ftc_disclosure_required: false,
    compliance_notes: ''
  });

  const [draftRoles, setDraftRoles] = useState({
    sales: {
      primary_role: 'brand_advocate',
      strength_score: 50,
      key_message: '',
      proof_points: [],
      best_use_cases: []
    },
    branding: {
      primary_role: 'brand_advocate',
      strength_score: 50,
      key_message: '',
      proof_points: [],
      best_use_cases: []
    },
    awareness: {
      primary_role: 'brand_advocate',
      strength_score: 50,
      key_message: '',
      proof_points: [],
      best_use_cases: []
    }
  });

  const [draftAssets, setDraftAssets] = useState({
    key_quotes: [],
    testimonials: [],
    success_stories: [],
    social_proof: [],
    media_mentions: [],
    video_links: [],
    audio_links: [],
    cta_phrases: []
  });

  const [hasChanges, setHasChanges] = useState({
    basic_info: false,
    story: false,
    legal: false,
    roles: false,
    assets: false
  });

  const [saving, setSaving] = useState({});

  // Load pack if editing
  useEffect(() => {
    if (isEditMode && packId) {
      loadPack();
    }
  }, [packId, isEditMode]);

  // Auto-populate usage rights based on category
  useEffect(() => {
    if (draftBasicInfo.ambassador_category === 'internal') {
      setDraftLegal(prev => ({
        ...prev,
        name_mention: true,
        story_sharing: true,
        image_visual: true,
        testimonial_quote: true,
        platform_tag: true,
        video_audio: true,
        legal_clearance_status: 'approved',
        ftc_disclosure_required: false
      }));
    } else {
      setDraftLegal(prev => ({
        ...prev,
        name_mention: true,
        story_sharing: false,
        image_visual: false,
        testimonial_quote: true,
        platform_tag: false,
        video_audio: false,
        legal_clearance_status: 'pending',
        ftc_disclosure_required: true
      }));
    }
  }, [draftBasicInfo.ambassador_category]);

  const loadPack = async () => {
    try {
      setLoading(true);
      const response = await getAmbassadorPackV4(packId);
      
      if (response.success && response.pack) {
        setPack(response.pack);
        setDraftBasicInfo(response.pack.basic_info);
        setDraftStory(response.pack.story_background);
        setDraftLegal(response.pack.usage_rights_legal);
        setDraftRoles(response.pack.role_profiles);
        setDraftAssets(response.pack.content_assets);
        toast.success('Ambassador pack loaded');
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load pack');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const markChanged = (section) => {
    setHasChanges(prev => ({ ...prev, [section]: true }));
  };

  const saveSection = async (sectionKey, data) => {
    try {
      setSaving(prev => ({ ...prev, [sectionKey]: true }));

      const updateData = {};
      
      if (sectionKey === 'basic_info') {
        updateData.basic_info = data;
      } else if (sectionKey === 'story') {
        updateData.story_background = data;
      } else if (sectionKey === 'legal') {
        updateData.usage_rights_legal = data;
      } else if (sectionKey === 'roles') {
        updateData.role_profiles = data;
      } else if (sectionKey === 'assets') {
        updateData.content_assets = data;
      }

      if (isEditMode) {
        const response = await updateAmbassadorPackV4(packId, updateData, sectionKey === 'basic_info' ? 'basic_info' : sectionKey === 'story' ? 'story_background' : sectionKey === 'legal' ? 'usage_rights_legal' : sectionKey === 'roles' ? 'role_profiles' : 'content_assets');
        
        if (response.success && response.pack) {
          setPack(response.pack);
          setHasChanges(prev => ({ ...prev, [sectionKey]: false }));
          toast.success(`Section saved`);
        }
      } else {
        // Only send basic_info for initial creation - backend will auto-fill other sections
        const createData = {
          basic_info: draftBasicInfo
        };

        const response = await createAmbassadorPackV4(createData);
        
        if (response.success && response.pack) {
          toast.success('Ambassador pack created');
          navigate(`/ambassador-packs/v4/${response.pack.pack_id}`);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.message || 'Failed to save');
    } finally {
      setSaving(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  const handlePublish = async () => {
    if (!isEditMode) {
      toast.error('Save the pack first');
      return;
    }

    try {
      const response = await publishAmbassadorPackV4(packId);
      if (response.success) {
        setPack(prev => ({ ...prev, status: 'published' }));
        toast.success('Published successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to publish');
    }
  };

  const handleUnpublish = async () => {
    if (!isEditMode) return;

    try {
      const response = await unpublishAmbassadorPackV4(packId);
      if (response.success) {
        setPack(prev => ({ ...prev, status: 'draft' }));
        toast.success('Unpublished successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to unpublish');
    }
  };

  // Note: Scrolling is enabled by NexusMobileFrame detecting this route
  // No useEffect needed here - the layout wrapper handles it

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
        <div>Loading ambassador pack...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      paddingBottom: '100px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
        
        {/* Top Navigation */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/packs')}
            className="text-blue-600 hover:underline text-sm font-medium"
          >
            ← Back to Knowledge Packs
          </button>
        </div>

      {/* Ambassador Content Header Banner */}
      <div style={{
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
        borderRadius: '8px',
        color: '#fff',
        marginBottom: '12px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>👤</span>
          <span>Ambassador Content - Profil Duta Jenama</span>
        </div>
        <div style={{ fontSize: '12px', lineHeight: '1.5', opacity: 0.95 }}>
          <strong>Apa yang perlu diisi:</strong> Maklumat lengkap tentang ambassador/influencer.
          <br/>
          <strong>Kenapa penting:</strong> AI guna untuk Script Generation, SOA Integration & Story Personalization.
          <br/>
          💡 <strong>Tip:</strong> Lengkapkan usage rights & role profiles untuk integration yang seamless!
        </div>
      </div>

      {/* Section 1: Basic Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
        <button
          type="button"
          onClick={() => toggleSection('basic_info')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">1️⃣ Basic Info</h3>
          <span className="text-gray-500">{openSections.basic_info ? '▼' : '▶'}</span>
        </button>
        
        {openSections.basic_info && (
          <BasicInfoSection
            data={draftBasicInfo}
            onChange={(newData) => {
              setDraftBasicInfo(newData);
              markChanged('basic_info');
            }}
            onSave={() => saveSection('basic_info', draftBasicInfo)}
            hasChanges={hasChanges.basic_info}
            saving={saving.basic_info}
          />
        )}
      </div>

      {/* Section 2: Story & Background */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
        <button
          type="button"
          onClick={() => toggleSection('story')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">2️⃣ Story & Background</h3>
          <span className="text-gray-500">{openSections.story ? '▼' : '▶'}</span>
        </button>
        
        {openSections.story && (
          <StorySection
            data={draftStory}
            onChange={(newData) => {
              setDraftStory(newData);
              markChanged('story');
            }}
            onSave={() => saveSection('story', draftStory)}
            hasChanges={hasChanges.story}
            saving={saving.story}
          />
        )}
      </div>

      {/* Section 3: Usage Rights & Legal */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
        <button
          type="button"
          onClick={() => toggleSection('legal')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">3️⃣ Usage Rights & Legal</h3>
          <span className="text-gray-500">{openSections.legal ? '▼' : '▶'}</span>
        </button>
        
        {openSections.legal && (
          <LegalSection
            data={draftLegal}
            category={draftBasicInfo.ambassador_category}
            onChange={(newData) => {
              setDraftLegal(newData);
              markChanged('legal');
            }}
            onSave={() => saveSection('legal', draftLegal)}
            hasChanges={hasChanges.legal}
            saving={saving.legal}
          />
        )}
      </div>

      {/* Section 4: Role Profiles */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
        <button
          type="button"
          onClick={() => toggleSection('roles')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">4️⃣ Role Profiles</h3>
          <span className="text-gray-500">{openSections.roles ? '▼' : '▶'}</span>
        </button>
        
        {openSections.roles && (
          <RolesSection
            data={draftRoles}
            onChange={(newData) => {
              setDraftRoles(newData);
              markChanged('roles');
            }}
            onSave={() => saveSection('roles', draftRoles)}
            hasChanges={hasChanges.roles}
            saving={saving.roles}
          />
        )}
      </div>

      {/* Section 5: Content Assets */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
        <button
          type="button"
          onClick={() => toggleSection('assets')}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
        >
          <h3 className="text-lg font-bold text-gray-800">5️⃣ Content Assets</h3>
          <span className="text-gray-500">{openSections.assets ? '▼' : '▶'}</span>
        </button>
        
        {openSections.assets && (
          <AssetsSection
            data={draftAssets}
            onChange={(newData) => {
              setDraftAssets(newData);
              markChanged('assets');
            }}
            onSave={() => saveSection('assets', draftAssets)}
            hasChanges={hasChanges.assets}
            saving={saving.assets}
          />
        )}
      </div>

      {/* Action Buttons Section - Similar to Product Pack V4 */}
      {isEditMode && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-600">Status:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  pack?.status === 'draft' 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : pack?.status === 'published' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {pack?.status === 'draft' ? '📝 Draft' : pack?.status === 'published' ? '✅ Published' : '📦 Archived'}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Publish/Unpublish Buttons */}
              {pack?.status === 'draft' && (
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to publish this ambassador pack? It will be visible to all users.')) {
                      await handlePublish();
                    }
                  }}
                  className="px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow-md"
                >
                  📤 Publish
                </button>
              )}
              
              {pack?.status === 'published' && (
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to unpublish this ambassador pack? It will no longer be visible to users.')) {
                      await handleUnpublish();
                    }
                  }}
                  className="px-4 py-2 text-sm font-semibold bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition shadow-md"
                >
                  📥 Unpublish
                </button>
              )}
              
              {/* Permanent Delete Button */}
              <button
                onClick={async () => {
                  const password = prompt('⚠️ PERMANENT DELETE - This action cannot be undone!\n\nEnter password to confirm permanent deletion:');
                  
                  if (!password) {
                    return; // User cancelled
                  }
                  
                  if (password !== 'emzi4111') {
                    alert('❌ Invalid password. Permanent deletion cancelled.');
                    return;
                  }
                  
                  if (window.confirm(`⚠️ FINAL CONFIRMATION\n\nAre you absolutely sure you want to PERMANENTLY DELETE this ambassador pack?\n\nPack ID: ${packId}\n\nThis action CANNOT be undone!`)) {
                    try {
                                            const response = await fetch(`${BACKEND_URL}/api/v4/ambassador-packs/${packId}`, {
                        method: 'DELETE',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
                        }
                      });
                      
                      if (response.ok) {
                        toast.success('Ambassador pack permanently deleted!');
                        // Redirect to pack list after deletion
                        navigate('/knowledge-packs?type=ambassador');
                      } else {
                        const error = await response.json();
                        throw new Error(error.detail || 'Failed to delete');
                      }
                    } catch (error) {
                      toast.error(`Failed to delete: ${error.message}`);
                      console.error('Delete error:', error);
                    }
                  }
                }}
                className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-md border-2 border-red-800"
              >
                🗑️ Permanent Delete
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">Created By</div>
              <div className="text-sm text-gray-800">
                {pack?.created_by || 'Unknown'}
              </div>
              <div className="text-xs text-gray-500">
                {pack?.created_at ? new Date(pack.created_at).toLocaleString() : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-600 mb-1">Last Modified By</div>
              <div className="text-sm text-gray-800">
                {pack?.last_modified_by || 'Unknown'}
              </div>
              <div className="text-xs text-gray-500">
                {pack?.updated_at ? new Date(pack.updated_at).toLocaleString() : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

const BasicInfoSection = ({ data, onChange, onSave, hasChanges, saving }) => {
  const INTERNAL_TYPES = [
    { value: 'founder', label: 'Founder' },
    { value: 'leadership', label: 'Leadership/Executive' },
    { value: 'team_member', label: 'Team Member/Employee' }
  ];
  
  const EXTERNAL_TYPES = [
    { value: 'external_expert', label: 'External Expert' },
    { value: 'influencer', label: 'Influencer' },
    { value: 'customer_advocate', label: 'Customer Advocate' }
  ];

  const availableTypes = data.ambassador_category === 'internal' ? INTERNAL_TYPES : EXTERNAL_TYPES;

  return (
    <div className="px-4 pb-4">
      <div className="space-y-4">
        
        {/* Category Buttons */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Ambassador Category <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onChange({ ...data, ambassador_category: 'internal', ambassador_type: 'founder' })}
              className={`p-3 rounded-lg border-2 font-medium transition ${
                data.ambassador_category === 'internal'
                  ? 'border-green-600 bg-green-50 text-green-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              🏢 Internal
            </button>
            <button
              onClick={() => onChange({ ...data, ambassador_category: 'external', ambassador_type: 'external_expert' })}
              className={`p-3 rounded-lg border-2 font-medium transition ${
                data.ambassador_category === 'external'
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              🌟 External
            </button>
          </div>
        </div>

        {/* Type */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Ambassador Type <span className="text-red-500">*</span>
          </label>
          <select
            value={data.ambassador_type}
            onChange={(e) => onChange({ ...data, ambassador_type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {availableTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Ambassador Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange({ ...data, name: e.target.value })}
            placeholder="e.g., Dr. Sarah Ahmad"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Role/Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.role_title}
            onChange={(e) => onChange({ ...data, role_title: e.target.value })}
            placeholder="e.g., Founder & CEO"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Short Bio <span className="text-red-500">*</span>
          </label>
          <textarea
            value={data.short_bio}
            onChange={(e) => onChange({ ...data, short_bio: e.target.value })}
            placeholder="2-3 sentence bio..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Photo URL */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Profile Photo URL
          </label>
          <input
            type="url"
            value={data.profile_photo_url}
            onChange={(e) => onChange({ ...data, profile_photo_url: e.target.value })}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Persona Brand */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            🎯 Personal Brand Statement
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 One-line statement that captures who they are and who they serve.
          </p>
          <input
            type="text"
            value={data.persona_brand || ''}
            onChange={(e) => onChange({ ...data, persona_brand: e.target.value })}
            placeholder="e.g., 'Health & Wellness Expert for Busy Moms' or 'Tech Entrepreneur Helping SMEs Scale'"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Market Influence Category */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            📊 Market Influence Level
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 Select the ambassador's reach and influence level in the market.
          </p>
          <select
            value={data.market_influence_category || 'micro'}
            onChange={(e) => onChange({ ...data, market_influence_category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="nano">Nano (&lt; 1K followers) - Highly engaged niche</option>
            <option value="micro">Micro (1K - 10K) - Strong community connection</option>
            <option value="mid_tier">Mid-Tier (10K - 100K) - Growing influence</option>
            <option value="macro">Macro (100K - 1M) - Broad reach</option>
            <option value="mega">Mega (&gt; 1M) - Mass market appeal</option>
            <option value="celebrity">Celebrity - Mainstream famous</option>
            <option value="expert">Expert - Industry authority (credential-based)</option>
            <option value="founder">Founder - Company owner/leader</option>
          </select>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onSave}
            disabled={!hasChanges || saving}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              hasChanges && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? '⏳ Saving...' : hasChanges ? '💾 Save' : '✓ Saved'}
          </button>
        </div>
      </div>
    </div>
  );
};

const StorySection = ({ data, onChange, onSave, hasChanges, saving }) => {
  return (
    <div className="px-4 pb-4">
      <div className="space-y-4">
        
        {/* Full Story */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Full Story <span className="text-red-500">*</span>
          </label>
          <textarea
            value={data.full_story}
            onChange={(e) => onChange({ ...data, full_story: e.target.value })}
            placeholder="Complete transformation narrative..."
            rows={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Current Achievement */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Current Achievement <span className="text-red-500">*</span>
          </label>
          <textarea
            value={data.current_achievement}
            onChange={(e) => onChange({ ...data, current_achievement: e.target.value })}
            placeholder="Where they are now..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Values */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Values & Beliefs (one per line)
          </label>
          <textarea
            value={data.values_beliefs.join('\n')}
            onChange={(e) => onChange({ ...data, values_beliefs: e.target.value.split('\n').filter(v => v.trim()) })}
            placeholder="Natural wellness\nFaith-based health"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Story Themes */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            📖 Story Themes (select 1-5 dominant themes)
          </label>
          <p className="text-xs text-gray-500 mb-3">
            💡 Select the main themes that run through this ambassador's story. This helps match them with relevant campaigns.
          </p>
          <div className="flex flex-wrap gap-2">
            {['transformation', 'perseverance', 'innovation', 'community', 'authenticity', 'expertise', 'family', 'health', 'success', 'purpose'].map(theme => (
              <button
                key={theme}
                type="button"
                onClick={() => {
                  const themes = data.story_themes || [];
                  const newThemes = themes.includes(theme)
                    ? themes.filter(t => t !== theme)
                    : themes.length < 5 ? [...themes, theme] : themes;
                  onChange({ ...data, story_themes: newThemes });
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  (data.story_themes || []).includes(theme)
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {(data.story_themes || []).includes(theme) ? '✓ ' : ''}{theme.charAt(0).toUpperCase() + theme.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">🎤 Voice & Tone Profile</h3>
          <p className="text-sm text-gray-600 mb-4">
            Define how this ambassador communicates. This guides AI script generation to match their authentic voice.
          </p>
        </div>

        {/* Primary Tone */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Primary Tone <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 The main speaking style that best describes this ambassador.
          </p>
          <select
            value={data.voice_tone_profile?.primary_tone || 'warm'}
            onChange={(e) => onChange({ 
              ...data, 
              voice_tone_profile: { ...(data.voice_tone_profile || {}), primary_tone: e.target.value }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="authoritative">Authoritative - Expert, commanding presence</option>
            <option value="warm">Warm - Friendly, approachable, caring</option>
            <option value="inspirational">Inspirational - Motivational, uplifting</option>
            <option value="conversational">Conversational - Casual, relatable chat</option>
            <option value="professional">Professional - Formal, polished business tone</option>
            <option value="authentic">Authentic - Real, vulnerable, honest</option>
            <option value="humorous">Humorous - Light, entertaining, witty</option>
          </select>
        </div>

        {/* Secondary Tone */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Secondary Tone (optional)
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 A supporting tone that adds depth to their communication style.
          </p>
          <select
            value={data.voice_tone_profile?.secondary_tone || ''}
            onChange={(e) => onChange({ 
              ...data, 
              voice_tone_profile: { ...(data.voice_tone_profile || {}), secondary_tone: e.target.value || null }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">None</option>
            <option value="analytical">Analytical - Data-driven, logical</option>
            <option value="storytelling">Storytelling - Narrative-focused</option>
            <option value="educational">Educational - Teaching-oriented</option>
            <option value="passionate">Passionate - Deeply emotional, intense</option>
            <option value="calm">Calm - Soothing, peaceful presence</option>
            <option value="energetic">Energetic - High energy, enthusiastic</option>
          </select>
        </div>

        {/* Formality Level Slider */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Formality Level: {data.voice_tone_profile?.formality_level || 50}
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 How formal or casual is their speaking style?
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-600 w-20">Casual</span>
            <input
              type="range"
              min="0"
              max="100"
              value={data.voice_tone_profile?.formality_level || 50}
              onChange={(e) => onChange({ 
                ...data, 
                voice_tone_profile: { ...(data.voice_tone_profile || {}), formality_level: parseInt(e.target.value) }
              })}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <span className="text-xs text-gray-600 w-20 text-right">Formal</span>
          </div>
        </div>

        {/* Energy Level Slider */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Energy Level: {data.voice_tone_profile?.energy_level || 50}
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 How calm or energetic is their delivery?
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-600 w-20">Calm</span>
            <input
              type="range"
              min="0"
              max="100"
              value={data.voice_tone_profile?.energy_level || 50}
              onChange={(e) => onChange({ 
                ...data, 
                voice_tone_profile: { ...(data.voice_tone_profile || {}), energy_level: parseInt(e.target.value) }
              })}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <span className="text-xs text-gray-600 w-20 text-right">Energetic</span>
          </div>
        </div>

        {/* Technicality Level Slider */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Technical Language Level: {data.voice_tone_profile?.technicality_level || 0}
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 How technical or simple is their language?
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-600 w-20">Simple</span>
            <input
              type="range"
              min="0"
              max="100"
              value={data.voice_tone_profile?.technicality_level || 0}
              onChange={(e) => onChange({ 
                ...data, 
                voice_tone_profile: { ...(data.voice_tone_profile || {}), technicality_level: parseInt(e.target.value) }
              })}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <span className="text-xs text-gray-600 w-20 text-right">Technical</span>
          </div>
        </div>

        {/* Speaking Pace */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Speaking Pace
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 How fast do they typically speak?
          </p>
          <div className="flex gap-2">
            {['slow', 'moderate', 'fast'].map(pace => (
              <button
                key={pace}
                type="button"
                onClick={() => onChange({ 
                  ...data, 
                  voice_tone_profile: { ...(data.voice_tone_profile || {}), speaking_pace: pace }
                })}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                  (data.voice_tone_profile?.speaking_pace || 'moderate') === pace
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {pace.charAt(0).toUpperCase() + pace.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Sentence Structure */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Sentence Structure
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 What's their typical sentence length and flow?
          </p>
          <div className="flex gap-2">
            {[
              { value: 'short_punchy', label: 'Short & Punchy' },
              { value: 'medium_balanced', label: 'Balanced' },
              { value: 'long_flowing', label: 'Long & Flowing' }
            ].map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ 
                  ...data, 
                  voice_tone_profile: { ...(data.voice_tone_profile || {}), sentence_structure: option.value }
                })}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                  (data.voice_tone_profile?.sentence_structure || 'medium_balanced') === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Catchphrases */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Signature Catchphrases (one per line, max 10)
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 Phrases they're known for saying. Examples: "Let's dive in!", "Here's the thing...", "Trust the process"
          </p>
          <textarea
            value={(data.voice_tone_profile?.catchphrases || []).join('\n')}
            onChange={(e) => {
              const phrases = e.target.value.split('\n').filter(p => p.trim()).slice(0, 10);
              onChange({ 
                ...data, 
                voice_tone_profile: { ...(data.voice_tone_profile || {}), catchphrases: phrases }
              });
            }}
            placeholder="Trust the process\nLet me show you how\nThis is game-changing"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Communication Quirks */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Communication Quirks (one per line)
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 Unique patterns in how they speak. Examples: "Uses lots of metaphors", "Asks rhetorical questions", "Pauses for emphasis"
          </p>
          <textarea
            value={(data.voice_tone_profile?.communication_quirks || []).join('\n')}
            onChange={(e) => {
              const quirks = e.target.value.split('\n').filter(q => q.trim());
              onChange({ 
                ...data, 
                voice_tone_profile: { ...(data.voice_tone_profile || {}), communication_quirks: quirks }
              });
            }}
            placeholder="Uses food metaphors frequently\nAsks rhetorical questions\nRepeats key points for emphasis"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Divider */}
        <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">💝 Emotional Signature</h3>
          <p className="text-sm text-gray-600 mb-4">
            Define the emotional profile for tone matching and content resonance. This helps AI match emotional context in scripts.
          </p>
        </div>

        {/* Primary Emotion */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Primary Emotion <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 The dominant emotional tone in their communication.
          </p>
          <select
            value={data.emotional_signature?.primary_emotion || 'trust'}
            onChange={(e) => onChange({ 
              ...data, 
              emotional_signature: { ...(data.emotional_signature || {}), primary_emotion: e.target.value }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="trust">Trust - Expert, reliable authority</option>
            <option value="excitement">Excitement - Energetic, enthusiastic</option>
            <option value="empathy">Empathy - Understanding, compassionate</option>
            <option value="confidence">Confidence - Strong, assured leadership</option>
            <option value="curiosity">Curiosity - Exploratory, questioning</option>
            <option value="inspiration">Inspiration - Uplifting, aspirational</option>
          </select>
        </div>

        {/* Secondary Emotion */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Secondary Emotion (optional)
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 A supporting emotional layer that adds nuance.
          </p>
          <select
            value={data.emotional_signature?.secondary_emotion || ''}
            onChange={(e) => onChange({ 
              ...data, 
              emotional_signature: { ...(data.emotional_signature || {}), secondary_emotion: e.target.value || null }
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">None</option>
            <option value="warmth">Warmth - Caring, nurturing</option>
            <option value="urgency">Urgency - Time-sensitive, action-driven</option>
            <option value="joy">Joy - Happy, celebratory</option>
            <option value="determination">Determination - Persistent, driven</option>
            <option value="hope">Hope - Optimistic, forward-looking</option>
            <option value="gratitude">Gratitude - Thankful, appreciative</option>
            <option value="calm">Calm - Peaceful, centered</option>
            <option value="passion">Passion - Deeply enthusiastic, fervent</option>
          </select>
        </div>

        {/* Emotional Intensity Slider */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Emotional Intensity: {data.emotional_signature?.emotional_intensity || 50}
          </label>
          <p className="text-xs text-gray-500 mb-2">
            💡 How strongly do they express emotions? (0 = subtle/understated, 100 = very intense/dramatic)
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-600 w-20">Subtle</span>
            <input
              type="range"
              min="0"
              max="100"
              value={data.emotional_signature?.emotional_intensity || 50}
              onChange={(e) => onChange({ 
                ...data, 
                emotional_signature: { ...(data.emotional_signature || {}), emotional_intensity: parseInt(e.target.value) }
              })}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <span className="text-xs text-gray-600 w-20 text-right">Intense</span>
          </div>
        </div>

        {/* Emotional Range */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Emotional Range (select all that apply)
          </label>
          <p className="text-xs text-gray-500 mb-3">
            💡 All emotions this ambassador comfortably expresses in content.
          </p>
          <div className="flex flex-wrap gap-2">
            {['warmth', 'urgency', 'joy', 'determination', 'hope', 'gratitude', 'calm', 'passion', 'curiosity', 'excitement', 'empathy', 'confidence'].map(emotion => (
              <button
                key={emotion}
                type="button"
                onClick={() => {
                  const range = data.emotional_signature?.emotional_range || [];
                  const newRange = range.includes(emotion)
                    ? range.filter(e => e !== emotion)
                    : [...range, emotion];
                  onChange({ 
                    ...data, 
                    emotional_signature: { ...(data.emotional_signature || {}), emotional_range: newRange }
                  });
                }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  (data.emotional_signature?.emotional_range || []).includes(emotion)
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {(data.emotional_signature?.emotional_range || []).includes(emotion) ? '✓ ' : ''}{emotion.charAt(0).toUpperCase() + emotion.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onSave}
            disabled={!hasChanges || saving}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              hasChanges && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? '⏳ Saving...' : hasChanges ? '💾 Save' : '✓ Saved'}
          </button>
        </div>
      </div>
    </div>
  );
};

const LegalSection = ({ data, category, onChange, onSave, hasChanges, saving }) => {
  return (
    <div className="px-4 pb-4">
      <div className="space-y-4">
        
        {/* Category Banner */}
        <div className={`p-3 rounded-lg ${
          category === 'internal' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'
        }`}>
          <p className="text-sm font-medium">
            {category === 'internal' 
              ? '🏢 Internal: All usage rights default to TRUE' 
              : '🌟 External: Conservative defaults, explicit permissions required'}
          </p>
        </div>

        {/* Permissions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Checkbox label="✅ Name Mention" checked={data.name_mention} onChange={(checked) => onChange({ ...data, name_mention: checked })} />
          <Checkbox label="📖 Story Sharing" checked={data.story_sharing} onChange={(checked) => onChange({ ...data, story_sharing: checked })} />
          <Checkbox label="📸 Image Use" checked={data.image_visual} onChange={(checked) => onChange({ ...data, image_visual: checked })} />
          <Checkbox label="💬 Testimonials" checked={data.testimonial_quote} onChange={(checked) => onChange({ ...data, testimonial_quote: checked })} />
          <Checkbox label="🏷️ Platform Tag" checked={data.platform_tag} onChange={(checked) => onChange({ ...data, platform_tag: checked })} />
          <Checkbox label="🎥 Video/Audio" checked={data.video_audio} onChange={(checked) => onChange({ ...data, video_audio: checked })} />
        </div>

        {/* Legal Status */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Legal Status</label>
          <select
            value={data.legal_clearance_status}
            onChange={(e) => onChange({ ...data, legal_clearance_status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="pending">⏳ Pending</option>
            <option value="approved">✅ Approved</option>
            <option value="expired">⚠️ Expired</option>
            <option value="revoked">❌ Revoked</option>
          </select>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onSave}
            disabled={!hasChanges || saving}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              hasChanges && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? '⏳ Saving...' : hasChanges ? '💾 Save' : '✓ Saved'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RolesSection = ({ data, onChange, onSave, hasChanges, saving }) => {
  const objectives = [
    { key: 'sales', title: '💰 SALES', color: 'green' },
    { key: 'branding', title: '🎯 BRANDING', color: 'blue' },
    { key: 'awareness', title: '📢 AWARENESS', color: 'orange' }
  ];

  return (
    <div className="px-4 pb-4">
      <div className="space-y-4">
        {objectives.map(obj => (
          <div key={obj.key} className={`p-4 border-2 rounded-lg ${
            obj.color === 'green' ? 'border-green-500 bg-green-50' :
            obj.color === 'blue' ? 'border-blue-500 bg-blue-50' :
            'border-orange-500 bg-orange-50'
          }`}>
            <h4 className="font-bold mb-3">{obj.title}</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Strength: {data[obj.key].strength_score}/100</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={data[obj.key].strength_score}
                  onChange={(e) => onChange({
                    ...data,
                    [obj.key]: { ...data[obj.key], strength_score: parseInt(e.target.value) }
                  })}
                  className="w-full"
                />
              </div>
              <textarea
                value={data[obj.key].key_message}
                onChange={(e) => onChange({
                  ...data,
                  [obj.key]: { ...data[obj.key], key_message: e.target.value }
                })}
                placeholder="Key message..."
                rows={2}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        ))}

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onSave}
            disabled={!hasChanges || saving}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              hasChanges && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? '⏳ Saving...' : hasChanges ? '💾 Save' : '✓ Saved'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AssetsSection = ({ data, onChange, onSave, hasChanges, saving }) => {
  const [showQuoteForm, setShowQuoteForm] = React.useState(false);
  const [editingQuoteIndex, setEditingQuoteIndex] = React.useState(null);
  const [quoteFormData, setQuoteFormData] = React.useState({
    text: '',
    category: 'product_endorsement',
    emotional_tone: 'satisfied',
    best_for_objective: ['branding'],
    context: '',
    approved_for_use: true
  });

  const addOrUpdateQuote = () => {
    if (!quoteFormData.text.trim()) return;
    
    const quotes = data.key_quotes || [];
    if (editingQuoteIndex !== null) {
      quotes[editingQuoteIndex] = quoteFormData;
    } else {
      quotes.push(quoteFormData);
    }
    
    onChange({ ...data, key_quotes: quotes });
    setShowQuoteForm(false);
    setEditingQuoteIndex(null);
    setQuoteFormData({
      text: '',
      category: 'product_endorsement',
      emotional_tone: 'satisfied',
      best_for_objective: ['branding'],
      context: '',
      approved_for_use: true
    });
  };

  const editQuote = (index) => {
    setQuoteFormData(data.key_quotes[index]);
    setEditingQuoteIndex(index);
    setShowQuoteForm(true);
  };

  const deleteQuote = (index) => {
    const quotes = [...data.key_quotes];
    quotes.splice(index, 1);
    onChange({ ...data, key_quotes: quotes });
  };

  return (
    <div className="px-4 pb-4">
      <div className="space-y-4">
        
        {/* Enhanced Quotes Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700">💬 Categorized Quotes</label>
              <p className="text-xs text-gray-500 mt-1">
                💡 Add quotes with context to help AI select the right quote for each campaign type.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowQuoteForm(true)}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition"
            >
              + Add Quote
            </button>
          </div>

          {/* Quote Form Modal */}
          {showQuoteForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  {editingQuoteIndex !== null ? 'Edit Quote' : 'Add New Quote'}
                </h3>
                
                <div className="space-y-4">
                  {/* Quote Text */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Quote Text <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={quoteFormData.text}
                      onChange={(e) => setQuoteFormData({ ...quoteFormData, text: e.target.value })}
                      placeholder="e.g., 'This product changed my life completely. I never thought I could feel this energized!'"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Quote Category
                    </label>
                    <p className="text-xs text-gray-500 mb-2">💡 What is this quote about?</p>
                    <select
                      value={quoteFormData.category}
                      onChange={(e) => setQuoteFormData({ ...quoteFormData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="product_endorsement">Product Endorsement - Direct praise</option>
                      <option value="transformation">Transformation - Before/after story</option>
                      <option value="pain_point">Pain Point - Problem identification</option>
                      <option value="solution_benefit">Solution Benefit - How it helped</option>
                      <option value="emotional_impact">Emotional Impact - How it made them feel</option>
                      <option value="results_proof">Results Proof - Data/metrics</option>
                      <option value="brand_values">Brand Values - Values alignment</option>
                      <option value="recommendation">Recommendation - Advising others</option>
                    </select>
                  </div>

                  {/* Emotional Tone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Emotional Tone
                    </label>
                    <p className="text-xs text-gray-500 mb-2">💡 What emotion does this quote convey?</p>
                    <select
                      value={quoteFormData.emotional_tone}
                      onChange={(e) => setQuoteFormData({ ...quoteFormData, emotional_tone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="excited">Excited - High energy, enthusiastic</option>
                      <option value="grateful">Grateful - Thankful, appreciative</option>
                      <option value="confident">Confident - Self-assured, empowered</option>
                      <option value="relieved">Relieved - Problem solved, stress-free</option>
                      <option value="surprised">Surprised - Unexpected positive outcome</option>
                      <option value="satisfied">Satisfied - Content, happy with result</option>
                      <option value="inspired">Inspired - Motivated, uplifted</option>
                      <option value="empowered">Empowered - Strong, capable</option>
                    </select>
                  </div>

                  {/* Best For Objectives */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Best For Campaign Objectives
                    </label>
                    <p className="text-xs text-gray-500 mb-2">💡 Which campaign types can use this quote?</p>
                    <div className="flex gap-2">
                      {['sales', 'branding', 'awareness'].map(obj => (
                        <button
                          key={obj}
                          type="button"
                          onClick={() => {
                            const current = quoteFormData.best_for_objective || [];
                            const updated = current.includes(obj)
                              ? current.filter(o => o !== obj)
                              : [...current, obj];
                            setQuoteFormData({ ...quoteFormData, best_for_objective: updated });
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                            (quoteFormData.best_for_objective || []).includes(obj)
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {obj.charAt(0).toUpperCase() + obj.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Context */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Context (optional)
                    </label>
                    <input
                      type="text"
                      value={quoteFormData.context || ''}
                      onChange={(e) => setQuoteFormData({ ...quoteFormData, context: e.target.value })}
                      placeholder="e.g., 'Said during product launch event'"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowQuoteForm(false);
                      setEditingQuoteIndex(null);
                      setQuoteFormData({
                        text: '',
                        category: 'product_endorsement',
                        emotional_tone: 'satisfied',
                        best_for_objective: ['branding'],
                        context: '',
                        approved_for_use: true
                      });
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={addOrUpdateQuote}
                    disabled={!quoteFormData.text.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {editingQuoteIndex !== null ? 'Update Quote' : 'Add Quote'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quotes List */}
          <div className="space-y-2 mt-3">
            {(data.key_quotes || []).map((quote, index) => (
              <div key={`item-${index}`} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm text-gray-800 mb-2">"{quote.text || quote}"</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {quote.category && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                          {quote.category.replace(/_/g, ' ')}
                        </span>
                      )}
                      {quote.emotional_tone && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {quote.emotional_tone}
                        </span>
                      )}
                      {quote.best_for_objective && quote.best_for_objective.length > 0 && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                          For: {quote.best_for_objective.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => editQuote(index)}
                      className="p-1 text-gray-600 hover:text-blue-600"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteQuote(index)}
                      className="p-1 text-gray-600 hover:text-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {(!data.key_quotes || data.key_quotes.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">
                No quotes added yet. Click "+ Add Quote" to get started.
              </p>
            )}
          </div>
        </div>

        {/* CTAs */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">🎯 CTA Phrases (one per line)</label>
          <p className="text-xs text-gray-500 mb-2">
            💡 Call-to-action phrases this ambassador uses. Examples: "Try it today!", "Join the journey", "Don't wait"
          </p>
          <textarea
            value={data.cta_phrases.join('\n')}
            onChange={(e) => onChange({ 
              ...data, 
              cta_phrases: e.target.value.split('\n').filter(p => p.trim())
            })}
            placeholder="Try it today!\nJoin the journey\nStart your transformation"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onSave}
            disabled={!hasChanges || saving}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              hasChanges && !saving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? '⏳ Saving...' : hasChanges ? '💾 Save' : '✓ Saved'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Checkbox = ({ label, checked, onChange }) => {
  return (
    <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4"
      />
      <span className="text-sm font-medium">{label}</span>
    </label>
  );
};

export default AmbassadorPackEditorV4;